import React, { useEffect, useState } from "react";
import { getDefaultImageLibrary, getSingleImage, uploadDeceasedDefaultImage } from "../../api/admin/imageReq";
import useAuth from "../../hooks/useAuth";
import useData from "../../hooks/useData";
import { useNavigate, useParams } from "react-router-dom";
import Missing from "../../pages/Missing";
import imageCompression from "browser-image-compression";
import {
    checkRequiredFields,
    fileToBase64,
    getErrorListFromValidation,
    patternListChoices,
    removeErrorList,
    validateFormInput
} from "../../helper/tools";
import Header from "../../components/common/Header";
import Icon from "../../components/common/Icon";

export default function FormImage({ data, legend }) {

    const imageFolder = process.env.REACT_APP_LOCAL_ASSETS_BASE_URL;

    const {
        singleImage, setSingleImage,
        imageLib, setImageLib
    } = useData();
    const [values, setValues] = useState(singleImage ? singleImage : {});
    const { auth } = useAuth();
    const token = auth?.token;
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedCompressedFile, setSelectedCompressedFile] = useState();
    const [fileName, setFileName] = useState();
    const [selectedImage, setSelectedImage] = useState();
    const [keywordList, setKeywordList] = useState();
    const [keyword, setKeyword] = useState();

    const onChange = (e) => {
        // Réinitialiser le nouveau keyword seulement si on sélectionne un keyword existant
        if ((e.target.name === "keyword" || e.target.name === "title") && e.target.value !== "") {
            setKeyword("");
        }
        setValues({ ...values, [e.target.name]: e.target.value })
        validateFormInput(e);
    };

    useEffect(() => {
        apiImageLib();
    }, []);

    const apiImageLib = async () => {
        const response = await getDefaultImageLibrary(token)
        setImageLib(response)
    }

    useEffect(() => {
        if (id != 0) {
            if (imageLib?.length > 0) {
                apiSingleImage();
            }
        } else {
            setSingleImage({})
            setValues({})
            setKeyword("")
            setSelectedCompressedFile(null)
            setFileName(null)
            setSelectedImage(null)
        }
    }, [id, imageLib]);

    const apiUploadedFile = async () => {
        let fileType;
        const category = Number(values?.category);
        if (category === 3) {
            fileType = "symbole"
        } else {
            fileType = "image"
        }

        const formData = new FormData();
        formData.append('file', selectedCompressedFile);
        formData.append('file_type', fileType);
        formData.append('category', category);
        formData.append('imageId', id);

        let finalTitle, finalKeyword;

        if (category === 3) {
            // Pour les symboles, utiliser le nouveau keyword/title saisi en priorité
            finalTitle = keyword && keyword.trim() !== "" ? keyword : (values?.title || "");
            formData.append('title', finalTitle);
            formData.append('keyword', finalTitle);
        } else {
            // Pour les images, séparer title et keyword
            formData.append('title', values?.title || "");
            finalKeyword = keyword && keyword.trim() !== "" ? keyword : (values?.keyword || "");
            formData.append('keyword', finalKeyword);
        }

        formData.append('alt', values?.alt || "");
        formData.append('fileName', fileName);

        try {
            const response = await uploadDeceasedDefaultImage(token, formData);
            removeErrorList();

            if (!response?.response?.data?.error) {
                const updatedValues = { ...values, image: response?.fileName };
                if (category === 3) {
                    updatedValues.title = finalTitle;
                } else {
                    updatedValues.keyword = finalKeyword;
                }
                setValues(updatedValues);
                await apiImageLib();
                setKeyword("");
                return response;
            } else {
                getErrorListFromValidation(response?.response?.data?.error);
                return response;
            }
        } catch (error) {
            console.error("Erreur upload:", error);
            throw error;
        }
    }

    useEffect(() => {
        if (imageLib) {
            const allFilteredKeywords = new Set();
            const category = Number(values?.category);

            if (category === 3) {
                // Symboles
                imageLib.filter(image => Number(image?.category) === 3).forEach(item => {
                    const k = item['title'];
                    if (k) allFilteredKeywords.add(k);
                });
            } else if (category === 1 || category === 2) {
                // Autres images
                imageLib.filter(image => Number(image?.category) !== 3).forEach(item => {
                    const k = item['keyword'];
                    if (k) allFilteredKeywords.add(k);
                });
            }

            setKeywordList(Array.from(allFilteredKeywords).sort());
        }
    }, [imageLib, values?.category]);

    const apiSingleImage = async () => {
        const response = await getSingleImage(token, id)
        setSingleImage(response)
    }


    useEffect(() => {
        setValues(singleImage)
    }, [singleImage]);

    const handleFileChange = async (e) => {
        let selectedFile = e.target.files[0];
        const options = {
            maxSizeMB: 10,
            maxWidthOrHeight: 250,
            useWebWorker: true
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setSelectedImage(reader.result)
        }
        reader.readAsDataURL(selectedFile)
        const compressedFile = await imageCompression(selectedFile, options);
        try {
            let base64StringConst = await fileToBase64(compressedFile);
            setFileName(e.target.value);
            setSelectedCompressedFile(base64StringConst)
            setValues({ ...values, image: fileName })
        } catch (error) {
            console.error("Erreur lors de la conversion en base64 : ", error);
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();
        if (!checkRequiredFields()) {
            return;
        }

        if (selectedCompressedFile || values?.url) {
            try {
                const response = await apiUploadedFile();

                if (!response?.response?.data?.error) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    navigate(-1);
                } else {
                    getErrorListFromValidation(response?.response?.data?.error);
                }
            } catch (error) {
                console.error("Erreur lors de l'upload:", error);
            }
        } else {
            alert(`Veuillez sélectionner une image`);
        }
    };

    const handleBack = (e) => {
        navigate(-1)
    };

    const onChangeKeyword = (e) => {
        setKeyword(e.target.value);
        validateFormInput(e);
    }

    /*const onImageLoaded = (e) => {
        const {width, height} = e.currentTarget;
        const cropWidthInPercent = (MIN_DIMENSION / width) * 100;
        const crop =makeAspectCrop(
            {
                unit: '%',
                width: cropWidthInPercent,
            },
            ASPECT_RATIO,
            width,
            height
        );
        const centeredCrop = centerCrop(crop, width, height)
        setCrop(centeredCrop)
    }*/

    return (
        <main className="admin">
            {(singleImage?.error && !singleImage?.id == 0) &&
                <Missing />
            }
            {(singleImage?.id || id == 0) &&
                <>
                    <Header
                        parent="Images"
                        location={id == 0 ? "Ajout d'une image" : "Modification d'une image"}
                        slug={`/admin/images/defunts`}
                    />
                    <form>
                        <div className="flex image-form">
                            <fieldset className="mini">
                                <Icon source="fa fa-image" span={id == 0 ? "Ajout d'une image" : "Modification d'une image"}/>
                                <div className="form-input">
                                    <label className="required">Catégorie de l'image</label>
                                    <select required={true} name="category" defaultValue={singleImage?.category} onChange={onChange}>
                                        <option name="category" value="">Choisir une catégorie</option>
                                        <option name="category" value="1">Image défunt</option>
                                        <option name="category" value="2">Image de faire-part</option>
                                        <option name="category" value="3">Symbole de faire-part</option>
                                    </select>
                                </div>
                                {values?.category != 3 &&
                                    <div className="form-input">
                                        <label htmlFor="title">Titre de l'image</label>
                                        <input required={false} type="text" name="title" id={1} defaultValue={values?.title} onChange={onChange} />
                                    </div>
                                }

                                <>
                                    {keywordList ?
                                        <div className="form-input">
                                            <label className="required" htmlFor={values?.category == 3 ? "title" : "keyword"}>{values?.category == 3 ? "Titre" : "Mot-clef"}</label>
                                            <select name={values?.category == 3 ? "title" : "keyword"} value={(values?.category == 3 ? values?.title : values?.keyword) ?? "placeholder"} onChange={onChange}>
                                                <option value="placeholder" disabled>Sélectionner un {values?.category == 3 ? "titre" : "mot-clef"}</option>
                                                <option value="" >Pas de {values?.category == 3 ? "titre" : "mot-clef"} correspondant</option>
                                                {keywordList?.map((k, index) => {
                                                    return (
                                                        <option key={index} value={k}>{k}</option>
                                                    )
                                                })}
                                            </select>
                                        </div> : <p>pas de {values?.category == 3 ? "Titre" : "Mot-clef"}</p>
                                    }
                                </>

                                {((values?.category == 3 && (!values?.title || values?.title == "")) || (values?.category != 3 && (!values?.keyword || values?.keyword == ""))) &&
                                    <div className="form-input">
                                        <label className="required" htmlFor={values?.category == 3 ? "title" : "keyword"}>Nouveau {values?.category == 3 ? "Titre pour le symbole" : "Mot-clef"}</label>
                                        <input required={true} type="text" name={values?.category == 3 ? "title" : "keyword"} value={keyword || ""} onChange={onChangeKeyword} onBlur={validateFormInput} />
                                        <span>(Bien vérifier qu'aucun {values?.category == 3 ? "Titre" : "Mot-clef"} approchant n'existe dans la liste)</span>
                                    </div>
                                }
                                <div className="form-input">
                                    <label htmlFor="alt">Courte description de l'image</label>
                                    <input pattern={patternListChoices?.name} required={false} type="text" name="alt" defaultValue={values?.alt} onChange={onChange} />
                                </div>
                                <div className="form-input image library">
                                    <label className="required" htmlFor="image">Image</label>
                                    <div>
                                        {selectedImage ?
                                            <img src={selectedImage} /> :
                                            values?.url &&
                                            <img src={`${imageFolder}/img/${values?.category == 1 ? "deceased_library" : values?.category == 2 ? "announcement_images_library/img" : values?.category == 3 ? "fpr/symboles-miniature" : ""}/${values?.url}`} alt={values?.alt} />
                                        }
                                        <div>
                                            <input type="file" name="image" accept="image/*" id="image" onChange={handleFileChange} />
                                        </div>
                                    </div>
                                </div>
                                <div className="errors"></div>
                            </fieldset>
                        </div>
                        <div className="buttons-container">
                            <button type="button" onClick={handleBack} className="red-button">
                                <i className="fa fa-xmark"></i>
                                <span>Annuler</span>
                            </button>
                            <button className="green-button upl-btn" onClick={handleFileUpload}>
                                <i className='fa fa-check'></i>
                                <span>Valider</span>
                            </button>
                        </div>
                    </form>
                </>
            }
        </main>

    )
}